import "react-datepicker/dist/react-datepicker.css";
import { useCallback, useMemo, useState } from "react";
import { ReportApi } from "src/services/api/ReportApi";
import {
    ColDef,
    IServerSideDatasource,
    IServerSideGetRowsParams,
    LoadSuccessParams,
    GridApi,
    SizeColumnsToContentStrategy,
    ValueFormatterParams,
    ValueFormatterFunc
} from "ag-grid-enterprise";

import useSidebar from "src/hooks/useSidebar";
import useReport from "src/hooks/useReport";

import BaseGrid from "../../BaseGrid";
import LoadingComponent from "src/components/LoadingComponent";
import SearchCriteriaRedReports from "src/components/SearchCriteriaRedReports";
import { downloadFile } from "src/utils/DownloadFile";
import { formatCurrency } from "src/utils/FormatCurrency";
import useReference from "src/hooks/useReference";
import { MastercardInterchangeIncomeSummaryDetailModel } from "src/models/MastercardInterchangeIncomeSummaryDetailModel";

const MastercardInterchangeIncomeSummary = () => {
    const { setIsOpen } = useSidebar();
    const { reportInputs, mapReportRequest, getContextMenuItems } = useReport();

    const [gridReady, setGridReady] = useState(false);
    const [gridApi, setGridApi] = useState<GridApi>();
    const [dataSource, setDataSource] = useState<IServerSideDatasource>();
    const [isDownload, setIsDownload] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const { currencyList } = useReference();

    const numericToAlpha = useMemo<Record<string, string>>(() => {
        const map: Record<string, string> = {};
        (currencyList ?? []).forEach((c: any) => {
            if (c?.numericCode != null && c?.alphaCode) {
                map[String(c.numericCode)] = String(c.alphaCode).toUpperCase();
            }
        });
        return map;
    }, [currencyList]);


    const resolveAlphaFromRow = useCallback((data?: MastercardInterchangeIncomeSummaryDetailModel | null): string => {
        const feeVal = data?.incomeCurrencyCode ?? data?.reconciliationCurrencyCode;
        if (feeVal == null) return "GBP";
        const str = String(feeVal).trim();
        if (/^[A-Za-z]{3}$/.test(str)) return str.toUpperCase();
        const mapped = numericToAlpha[String(str)];
        if (mapped) return mapped;
        return "GBP";
    }, [numericToAlpha]);


    const currencyFormatter = useCallback<ValueFormatterFunc<MastercardInterchangeIncomeSummaryDetailModel, any>>(
        (params: ValueFormatterParams<MastercardInterchangeIncomeSummaryDetailModel, any>): string => {
            const v = params?.value;
            if (v === null || v === undefined) return "";
            const n = typeof v === "number" ? v : Number(v);
            if (Number.isNaN(n)) return "";
            const alphaCode = resolveAlphaFromRow(params.data);
            return String(formatCurrency(n, alphaCode));
        },
        [resolveAlphaFromRow]
    );

    const colDefs = useMemo<ColDef<MastercardInterchangeIncomeSummaryDetailModel>[]>(() => [
        { headerName: "IRD", field: "ird" },
        { headerName: "Interchange Program", field: "program" },
        { headerName: "Count", field: "count" },
        { headerName: "Transaction Amount", field: "reconciliationAmount", valueFormatter: currencyFormatter },
        { headerName: "Income Amount", field: "incomeAmount", valueFormatter: currencyFormatter }
    ], [currencyFormatter]);


    const defaultColDef: ColDef = {
        sortable: false,
        filter: false,
        cellStyle: { textAlign: "center" },
    };

    const autoSizeStrategy = useMemo<SizeColumnsToContentStrategy>(() => {
        return {
            type: "fitCellContents",
            defaultMinWidth: 120,
            defaultMaxWidth: 300,
            skipHeader: false,
        };
    }, []);

    const handleViewReport = () => {
        setIsDownload(false);
        setGridReady(true);
        setDataSource(createServerSideDatasource());
    };

    const handleDownloadReport = () => {
        setIsDownload(true);
        setIsLoading(true);
        ReportApi.exportMastercardInterchangeIncomeSummary(mapReportRequest(null))
            .then((res: any) => {
                downloadFile(MastercardInterchangeIncomeSummary.displayName!, res.data);
            })
            .finally(() => setIsLoading(false));
    };

    const createServerSideDatasource = (): IServerSideDatasource => {
        return {
            getRows: (params: IServerSideGetRowsParams) => {
                getReportData(params);
            },
        };
    };

    const getReportData = (params: IServerSideGetRowsParams | null) => {
        setIsOpen(false);

        ReportApi.getMastercardInterchangeIncomeSummary(mapReportRequest(params))
            .then((res) => {
                const result = res.data;
                const success: LoadSuccessParams = {
                    rowData: result.rows,
                    rowCount: result.totalCount,
                };
                if (result?.rows?.length > 0) {
                    params?.api?.hideOverlay();
                    params?.success(success);
                } else {
                    params?.api?.showNoRowsOverlay();
                    params?.success({ rowData: [], rowCount: 0 });
                }

                // Add pinned bottom totals row if summary detail provided
                if (result.summaryDetail?.totalAmounts) {
                    const summaryRows = result.summaryDetail.totalAmounts?.map((item) => ({
                        ird: "Totals",
                        count: item.totalCount,
                        reconciliationAmount: item.totalReconciliationAmount,
                        incomeAmount: item.totalIncomeAmount,
                        incomeCurrencyCode: item.incomeCurrencyCode
                    }));
                
                    (params?.api as any).setGridOption('pinnedBottomRowData', summaryRows)
                } else {
                    (params?.api as any).setGridOption('pinnedBottomRowData', []);
                }
            })
            .catch(() => params?.fail?.());
    };

    return (
        <>
            <div className="report-container">
                <div className="report-header">
                    <h5 className="mb-0">Mastercard Interchange Income Summary Report</h5>
                </div>
                <div className="blocks">

                    <SearchCriteriaRedReports
                        handleViewReport={handleViewReport}
                        handleDownloadReport={handleDownloadReport}
                        showToDate={true}
                        useIcaValue={true}
                    />
                </div>
                {isDownload && isLoading ? (
                    <LoadingComponent />
                ) : (
                    !isDownload && (
                        <BaseGrid
                            colDefs={colDefs}
                            defaultColDef={defaultColDef}
                            getData={getReportData}
                            isGridReady={gridReady}
                            reportInputs={reportInputs}
                            dataSource={dataSource}
                            gridApi={gridApi}
                            setGridApi={setGridApi}
                            getContextMenuItems={getContextMenuItems}
                            autoSizeStrategy={autoSizeStrategy}
                        />
                    )
                )}
            </div>
        </>
    );


};


MastercardInterchangeIncomeSummary.displayName = "MastercardInterchangeIncomeSummary";

export default MastercardInterchangeIncomeSummary;